<template>
  <v-dialog
    :value="value"
    @input="$emit('input', $event)"
    max-width="1000"
    scrollable
  >
    <v-card>
      <v-card-title class="py-3 d-flex align-center">
        <v-icon class="mr-2">mdi-history</v-icon>
        Execution History
        <span v-if="dataSource" class="text-body-2 text--secondary ml-2">
          — {{ dataSource.name }}
        </span>
        <v-spacer />
        <v-btn icon small @click="$emit('input', false)">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>

      <v-divider />

      <v-card-text class="pa-0">
        <v-data-table
          :headers="headers"
          :items="history"
          :loading="loading"
          :server-items-length="total"
          :options.sync="tableOptions"
          dense
          class="execution-history-table"
        >
          <!-- Status -->
          <template v-slot:[`item.status`]="{ item }">
            <v-chip
              x-small
              :color="statusMap[item.status].color"
              dark
            >
              {{ statusMap[item.status].label }}
            </v-chip>
          </template>

          <!-- Trigger -->
          <template v-slot:[`item.triggerType`]="{ item }">
            <v-icon x-small class="mr-1">
              {{ triggerTypeMap[item.triggerType].icon }}
            </v-icon>
            <span class="text-caption">{{ triggerTypeMap[item.triggerType].label }}</span>
          </template>

          <!-- Started At -->
          <template v-slot:[`item.startedAt`]="{ item }">
            <span class="text-caption">{{ formatDate(item.startedAt) }}</span>
          </template>

          <!-- Duration -->
          <template v-slot:[`item.duration`]="{ item }">
            <span class="text-caption">{{ formatDuration(item) }}</span>
          </template>

          <!-- Records -->
          <template v-slot:[`item.records`]="{ item }">
            <span class="text-caption">
              <span class="success--text font-weight-medium">{{ item.recordsImported || 0 }}</span>
              <span v-if="item.recordsUpdated"> / {{ item.recordsUpdated }} upd</span>
              <span v-if="item.recordsFailed" class="error--text ml-1">
                ({{ item.recordsFailed }} failed)
              </span>
            </span>
          </template>

          <!-- Error Details -->
          <template v-slot:[`item.errorDetails`]="{ item }">
            <slot name="execution-details" :execution="item">
            <v-tooltip v-if="item.errorDetails" bottom max-width="400">
              <template v-slot:activator="{ on }">
                <div v-on="on" class="d-flex align-center" style="cursor: pointer;">
                  <v-icon x-small color="error" class="mr-1 flex-shrink-0">mdi-alert-circle-outline</v-icon>
                  <span class="text-caption error--text text-truncate" style="max-width: 250px;">
                    {{ item.errorDetails }}
                  </span>
                </div>
              </template>
              <span>{{ item.errorDetails }}</span>
            </v-tooltip>
            <span v-else class="text-caption text--disabled">—</span>
            </slot>
          </template>

          <!-- Empty state -->
          <template v-slot:no-data>
            <div class="text-center py-6">
              <v-icon size="40" color="grey lighten-1">mdi-history</v-icon>
              <div class="text-body-2 text--secondary mt-2">
                No executions recorded yet.
              </div>
            </div>
          </template>
        </v-data-table>
      </v-card-text>
    </v-card>
  </v-dialog>
</template>

<script>
export default {
  name: 'SharedApiExecutionHistory',
  props: {
    value: {
      type: Boolean,
      default: false,
    },
    dataSource: {
      type: Object,
      default: null,
    },
    history: {
      type: Array,
      default: () => [],
    },
    total: {
      type: Number,
      default: 0,
    },
    loading: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      statusMap: {
        0: { label: 'Running', color: 'info' },
        1: { label: 'Success', color: 'success' },
        2: { label: 'Partial Success', color: 'orange' },
        3: { label: 'Failed', color: 'error' },
      },
      triggerTypeMap: {
        0: { label: 'Manual', icon: 'mdi-hand-back-right' },
        1: { label: 'Scheduled', icon: 'mdi-clock-outline' },
      },
      tableOptions: {
        page: 1,
        itemsPerPage: 10,
        sortBy: ['startedAt'],
        sortDesc: [true],
      },
      headers: [
        { text: 'Started', value: 'startedAt', width: '150px' },
        { text: 'Status', value: 'status', width: '110px', sortable: false },
        { text: 'Trigger', value: 'triggerType', width: '100px', sortable: false },
        { text: 'Duration', value: 'duration', width: '90px', sortable: false },
        { text: 'Records', value: 'records', width: '160px', sortable: false },
        { text: 'Error Details', value: 'errorDetails', width: '280px', sortable: false },
      ],
    };
  },
  watch: {
    value(open) {
      if (open && this.dataSource) {
        this.tableOptions.page = 1;
        this.emitFetch();
      }
    },
    tableOptions: {
      deep: true,
      handler() {
        if (this.value && this.dataSource) {
          this.emitFetch();
        }
      },
    },
  },
  methods: {
    emitFetch() {
      this.$emit('fetch-history', {
        dataSourceId: this.dataSource.id,
        params: {
          page: this.tableOptions.page,
          itemsPerPage: this.tableOptions.itemsPerPage,
        },
      });
    },
    formatDate(dateStr) {
      if (!dateStr) return '';
      const d = new Date(dateStr);
      return d.toLocaleDateString() + ' ' + d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    },
    formatDuration(item) {
      if (!item.startedAt || !item.completedAt) return '—';
      const ms = new Date(item.completedAt) - new Date(item.startedAt);
      if (ms < 1000) return `${ms}ms`;
      if (ms < 60000) return `${(ms / 1000).toFixed(1)}s`;
      return `${Math.floor(ms / 60000)}m ${Math.floor((ms % 60000) / 1000)}s`;
    },
  },
};
</script>
